import Phaser from 'phaser';
import { frameKey, heroFrameSet } from './assets';
import { drawClaws } from './icons/clawsIcon';
import { drawAtom } from './icons/atomIcon';
import { drawBolt } from './icons/boltIcon';
import { drawAdamantium } from './icons/adamantiumIcon';
import { drawBlaster } from './icons/blasterIcon';
import { drawEye } from './icons/eyeIcon';
import { drawFist } from './icons/fistIcon';
import { drawGhostWeb } from './icons/ghostWebIcon';
import { drawHammer } from './icons/hammerIcon';
import { drawHex } from './icons/hexIcon';
import { drawRepulsor } from './icons/repulsorIcon';
import { drawShield } from './icons/shieldIcon';
import { drawWeb } from './icons/webIcon';
import { drawBillyclub } from './icons/billyclubIcon';
import { drawFlame } from './icons/flameIcon';
import { drawPowerSword } from './icons/powerSwordIcon';
import { drawDragonDagger } from './icons/dragonDaggerIcon';
import { drawPowerBow } from './icons/powerBowIcon';
import { HeroId, HEROES, RosterHeroId } from './heroes';
// HeroId imported for narrowing via `id in HEROES` guards below
import { loadSave } from './save';
import { getCurrentMode } from './mode';
import { TileKind } from './types';

const PORTRAIT_R = 52;
const NATIVE_SPRITE = 92;
const C_CARD = 0x121a32;
const C_RING_UNLOCKED = 0xffd24a;
const C_RING_LOCKED = 0x2a3558;
const C_BADGE_BG = 0x0a0f1f;
const C_STROKE = 0x0a0f1f;

type SlotDef = {
  heroId: RosterHeroId;
  label: string;
};

const SLOTS_MARVEL: SlotDef[] = [
  { heroId: 'captain-america', label: 'Cap' },
  { heroId: 'black-panther', label: 'Panther' },
  { heroId: 'iron-man', label: 'Iron Man' },
  { heroId: 'spider-man', label: 'Spidey' },
  { heroId: 'thor', label: 'Thor' },
  { heroId: 'hulk', label: 'Hulk' },
  { heroId: 'doctor-strange', label: 'Strange' },
  { heroId: 'scarlet-witch', label: 'Witch' },
  { heroId: 'captain-marvel', label: 'Cpt Marvel' },
  { heroId: 'vision', label: 'Vision' },
  { heroId: 'ant-man', label: 'Ant-Man' },
  { heroId: 'ghost-spider', label: 'Ghost-Spider' },
  { heroId: 'spin', label: 'Spin' },
  { heroId: 'wolverine', label: 'Wolverine' },
  { heroId: 'rocket', label: 'Rocket' },
  { heroId: 'daredevil', label: 'Daredevil' },
  { heroId: 'human-torch', label: 'Torch' },
];

const SLOTS_POWERRANGERS: SlotDef[] = [
  { heroId: 'red-ranger', label: 'Red' },
  { heroId: 'green-ranger', label: 'Green' },
  { heroId: 'pink-ranger', label: 'Pink' },
];

const SPRITE_AVAILABLE: Record<RosterHeroId, boolean> = {
  'captain-america': true,
  'black-panther': true,
  'iron-man': true,
  'spider-man': true,
  thor: true,
  hulk: true,
  'doctor-strange': true,
  'scarlet-witch': true,
  'captain-marvel': true,
  vision: true,
  'ant-man': true,
  'ghost-spider': true,
  spin: true,
  wolverine: true,
  rocket: true,
  daredevil: true,
  'human-torch': true,
  'red-ranger': true,
  'green-ranger': true,
  'pink-ranger': true,
};

export class RosterScene extends Phaser.Scene {
  constructor() {
    super('Roster');
  }

  preload() {
    for (const slot of [...SLOTS_MARVEL, ...SLOTS_POWERRANGERS]) {
      if (!SPRITE_AVAILABLE[slot.heroId]) continue;
      const set = getIdleFrameSet(slot.heroId);
      if (set && set.urls.length > 0) {
        this.load.image(frameKey(set.prefix, 0), set.urls[0]);
      }
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');
    this.layout();
    this.scale.on('resize', () => {
      this.children.removeAll(true);
      this.layout();
    });
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    this.add
      .text(w / 2, h * 0.1, 'Heroes', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '34px',
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.drawBackButton();
    this.drawGrid(w, h);
  }

  private drawBackButton() {
    const g = this.add.graphics();
    const cx = 32;
    const cy = 32;
    g.fillStyle(C_CARD, 1);
    g.lineStyle(2, C_STROKE, 1);
    g.fillCircle(cx, cy, 22);
    g.strokeCircle(cx, cy, 22);
    g.lineStyle(3, 0xffffff, 1);
    g.beginPath();
    g.moveTo(cx + 6, cy - 8);
    g.lineTo(cx - 6, cy);
    g.lineTo(cx + 6, cy + 8);
    g.strokePath();
    const hit = this.add.zone(cx, cy, 56, 56).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('LevelMap'));
  }

  private drawGrid(w: number, h: number) {
    const save = loadSave();
    const cols = 5;
    const rows = 2;
    const slotW = Math.min(140, (w * 0.92) / cols);
    const slotH = 160;
    const totalW = slotW * cols;
    const totalH = slotH * rows;
    const startX = Math.floor((w - totalW) / 2 + slotW / 2);
    const startY = Math.floor((h - totalH) / 2 + slotH / 2 + 20);

    const mode = getCurrentMode();
    const slots = mode === 'powerrangers' ? SLOTS_POWERRANGERS : SLOTS_MARVEL;
    slots.forEach((slot, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * slotW;
      const y = startY + row * slotH;
      const unlocked = isHeroUnlocked(save.unlockedHeroes, slot.heroId);
      this.drawSlot(x, y, slot, unlocked);
    });
  }

  private drawSlot(x: number, y: number, slot: SlotDef, unlocked: boolean) {
    const container = this.add.container(x, y);

    const ring = this.add.graphics();
    ring.fillStyle(C_CARD, 1);
    ring.lineStyle(4, unlocked ? C_RING_UNLOCKED : C_RING_LOCKED, 1);
    ring.fillCircle(0, 0, PORTRAIT_R);
    ring.strokeCircle(0, 0, PORTRAIT_R);
    container.add(ring);

    if (unlocked && SPRITE_AVAILABLE[slot.heroId]) {
      const set = getIdleFrameSet(slot.heroId);
      if (set && set.urls.length > 0 && this.textures.exists(frameKey(set.prefix, 0))) {
        const sprite = this.add.sprite(0, 0, frameKey(set.prefix, 0));
        const scale = (PORTRAIT_R * 1.55) / NATIVE_SPRITE;
        sprite.setScale(scale);
        container.add(sprite);
      }
    } else {
      const silhouette = this.add.graphics();
      silhouette.fillStyle(0x2a3558, 1);
      silhouette.fillCircle(0, -4, PORTRAIT_R * 0.55);
      drawPadlock(silhouette, 0, PORTRAIT_R * 0.1, PORTRAIT_R * 0.5, 0x64748b);
      container.add(silhouette);
    }

    if (unlocked) {
      const kind = getHeroTileKind(slot.heroId);
      if (kind) {
        const badge = this.add.graphics();
        const bx = PORTRAIT_R * 0.7;
        const by = PORTRAIT_R * 0.7;
        badge.fillStyle(C_BADGE_BG, 1);
        badge.lineStyle(2, C_STROKE, 1);
        badge.fillCircle(bx, by, 18);
        badge.strokeCircle(bx, by, 18);
        const icon = this.add.graphics();
        icon.setPosition(bx, by);
        drawTileKindMini(icon, kind, 26);
        container.add(badge);
        container.add(icon);
      }
    }

    const label = this.add.text(0, PORTRAIT_R + 22, slot.label, {
      fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
      fontSize: '16px',
      color: unlocked ? '#f4f4f4' : '#64748b',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(label);

    if (unlocked) {
      const hit = this.add.zone(0, 0, PORTRAIT_R * 2.4, PORTRAIT_R * 2.4 + 40).setInteractive({
        useHandCursor: true,
      });
      hit.on('pointerup', () => {
        this.tweens.add({
          targets: container,
          scale: 1.1,
          duration: 120,
          yoyo: true,
          ease: 'Sine.easeOut',
        });
      });
      container.add(hit);
    }
  }
}

function getIdleFrameSet(heroId: RosterHeroId) {
  if (heroId in HEROES) return HEROES[heroId as HeroId].idleFrames();
  return heroFrameSet(heroId, 'idle');
}

function getHeroTileKind(heroId: RosterHeroId): TileKind | null {
  if (heroId in HEROES) return HEROES[heroId as HeroId].tileKind;
  return null;
}

function isHeroUnlocked(unlocked: RosterHeroId[], id: RosterHeroId): boolean {
  return unlocked.includes(id);
}

function drawTileKindMini(g: Phaser.GameObjects.Graphics, kind: TileKind, size: number) {
  switch (kind) {
    case 'shield':
      drawShield(g, size);
      return;
    case 'claws':
      drawClaws(g, size);
      return;
    case 'repulsor':
      drawRepulsor(g, size);
      return;
    case 'web':
      drawWeb(g, size);
      return;
    case 'hammer':
      drawHammer(g, size);
      return;
    case 'fist':
      drawFist(g, size);
      return;
    case 'eye':
      drawEye(g, size);
      return;
    case 'hex':
      drawHex(g, size);
      return;
    case 'atom':
      drawAtom(g, size);
      return;
    case 'ghost-web':
      drawGhostWeb(g, size);
      return;
    case 'bolt':
      drawBolt(g, size);
      return;
    case 'adamantium':
      drawAdamantium(g, size);
      return;
    case 'blaster':
      drawBlaster(g, size);
      return;
    case 'billyclub':
      drawBillyclub(g, size);
      return;
    case 'flame':
      drawFlame(g, size);
      return;
    case 'power-sword':
      drawPowerSword(g, size);
      return;
    case 'dragon-dagger':
      drawDragonDagger(g, size);
      return;
    case 'power-bow':
      drawPowerBow(g, size);
      return;
    default:
      return;
  }
}

function drawPadlock(g: Phaser.GameObjects.Graphics, cx: number, cy: number, size: number, color: number) {
  const bodyW = size;
  const bodyH = size * 0.8;
  const shackleR = size * 0.38;
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - bodyW / 2, cy - bodyH / 2 + shackleR * 0.3, bodyW, bodyH, 3);
  g.lineStyle(Math.max(2, Math.floor(size * 0.16)), color, 1);
  g.beginPath();
  g.arc(cx, cy - bodyH / 2 + shackleR * 0.1, shackleR, Math.PI, 0, false);
  g.strokePath();
}
